import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';

// ─────────────────────────────────────────────────────────────
//  Panel admin: Estancias de agosto (estilo hotel). El precio lo fija el admin
//  (viene de un presupuesto del CRM) y se cobra en 2 plazos: señal (20%) + resto.
//  La familia paga desde "Mis reservas" tras iniciar sesión y ser asignada.
// ─────────────────────────────────────────────────────────────

const money = n => `${(Number(n) || 0).toFixed(2)} €`;

const inputStyle = {
  width: '100%', padding: '.5rem .6rem', borderRadius: 6,
  border: '1px solid rgba(255,255,255,.18)', background: 'rgba(0,0,0,.25)',
  color: 'var(--white)', fontSize: '.9rem',
};

function Field({ label, children }) {
  return (
    <label style={{ display: 'block', fontSize: '.78rem', color: 'var(--mid)' }}>
      {label}
      <div style={{ marginTop: '.25rem' }}>{children}</div>
    </label>
  );
}

const EMPTY = {
  contactEmail: '', familyName: '', checkInDate: '', checkOutDate: '',
  numAdults: '', numChildren: '', numInfants: '', concepto: '', total: '',
  metodoPago: 'stripe',
};

export default function AdminEstancias() {
  const { token } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [creando, setCreando] = useState(false);
  const [estancias, setEstancias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await api.getEstancias(token);
      setEstancias(r.estancias || []);
    } catch (e) {
      setError(e.message || 'No se pudieron cargar las estancias.');
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const totalNum = Number(form.total) || 0;
  const senal = Math.round(totalNum * 20) / 100;

  const crear = async () => {
    if (!form.contactEmail.trim()) { alert('Falta el email de la familia.'); return; }
    if (!(Number(form.total) > 0)) { alert('Pon un importe total válido.'); return; }
    setCreando(true);
    try {
      const r = await api.createEstancia(token, {
        contactEmail: form.contactEmail.trim(),
        familyName: form.familyName.trim(),
        checkInDate: form.checkInDate || null,
        checkOutDate: form.checkOutDate || null,
        numAdults: Number(form.numAdults) || 0,
        numChildren: Number(form.numChildren) || 0,
        numInfants: Number(form.numInfants) || 0,
        concepto: form.concepto.trim(),
        total: Number(form.total),
        senalPct: 20,
        metodoPago: form.metodoPago,
      });
      alert(r.asignada
        ? `Estancia creada y asignada a la familia. Señal ${money(r.senal)} · resto ${money(r.resto)}.`
        : `Estancia creada (⚠️ pendiente de asignar: la familia aún no ha iniciado sesión). Señal ${money(r.senal)} · resto ${money(r.resto)}.`);
      setForm(EMPTY);
      await load();
    } catch (e) {
      alert(e.message || 'No se pudo crear la estancia.');
    } finally { setCreando(false); }
  };

  const asignar = async (e) => {
    const email = window.prompt('Email de la familia (debe haber iniciado sesión en reservas):', e.contactEmail || '');
    if (!email) return;
    try {
      const r = await api.assignEstancia(token, e.id, email.trim());
      alert(r.message || 'Asignada.');
      await load();
    } catch (err) {
      alert(err.message || 'No se pudo asignar.');
    }
  };

  // Marca/desmarca un plazo como cobrado (sobre todo para transferencias).
  const marcarPlazo = async (e, planId, paid) => {
    try {
      await api.markEstanciaPlazo(token, e.id, planId, paid);
      await load();
    } catch (err) {
      alert(err.message || 'No se pudo actualizar el plazo.');
    }
  };

  return (
    <div className="main-content page-enter">
      <div style={{ marginBottom: '1rem' }}>
        <div className="section-label">Estancias de agosto</div>
        <h1 style={{ fontSize: '1.4rem', color: 'var(--white)', margin: 0 }}>Estancias familiares (estilo hotel)</h1>
        <p style={{ color: 'var(--mid)', fontSize: '.85rem', marginTop: '.3rem' }}>
          Fija el precio acordado en el presupuesto; se cobra en 2 plazos: <strong>señal 20%</strong> + resto.
          La familia paga desde “Mis reservas” tras iniciar sesión.
        </p>
      </div>

      {/* Formulario nueva estancia */}
      <div className="card" style={{ padding: '1rem', marginBottom: '1.2rem' }}>
        <div style={{ color: 'var(--white)', fontWeight: 600, marginBottom: '.7rem' }}>➕ Nueva estancia</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '.7rem' }}>
          <Field label="Email de la familia *">
            <input style={inputStyle} type="email" value={form.contactEmail} onChange={e => set('contactEmail', e.target.value)} placeholder="familia@correo.com" />
          </Field>
          <Field label="Nombre de la familia">
            <input style={inputStyle} value={form.familyName} onChange={e => set('familyName', e.target.value)} placeholder="Apellidos" />
          </Field>
          <Field label="Entrada">
            <input style={inputStyle} type="date" value={form.checkInDate} onChange={e => set('checkInDate', e.target.value)} />
          </Field>
          <Field label="Salida">
            <input style={inputStyle} type="date" value={form.checkOutDate} onChange={e => set('checkOutDate', e.target.value)} />
          </Field>
          <Field label="Adultos">
            <input style={inputStyle} type="number" min="0" value={form.numAdults} onChange={e => set('numAdults', e.target.value)} />
          </Field>
          <Field label="Niños">
            <input style={inputStyle} type="number" min="0" value={form.numChildren} onChange={e => set('numChildren', e.target.value)} />
          </Field>
          <Field label="Bebés">
            <input style={inputStyle} type="number" min="0" value={form.numInfants} onChange={e => set('numInfants', e.target.value)} />
          </Field>
          <Field label="Total acordado (€) *">
            <input style={inputStyle} type="number" min="0" step="0.01" value={form.total} onChange={e => set('total', e.target.value)} placeholder="0.00" />
          </Field>
          <Field label="Método de pago">
            <select style={inputStyle} value={form.metodoPago} onChange={e => set('metodoPago', e.target.value)}>
              <option value="stripe">💳 Stripe (pago online)</option>
              <option value="transferencia">🏦 Transferencia</option>
            </select>
          </Field>
        </div>
        <div style={{ marginTop: '.7rem' }}>
          <Field label="Concepto (líneas del presupuesto / lo que incluye)">
            <textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} value={form.concepto} onChange={e => set('concepto', e.target.value)} placeholder="Ej: 4 noches pensión completa · 2 adultos + 2 niños · actividades incluidas…" />
          </Field>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '.6rem', marginTop: '.8rem' }}>
          <div style={{ fontSize: '.82rem', color: 'var(--mid)' }}>
            {totalNum > 0 && <>Señal (20%): <strong style={{ color: 'var(--white)' }}>{money(senal)}</strong> · Resto: <strong style={{ color: 'var(--white)' }}>{money(totalNum - senal)}</strong></>}
          </div>
          <button className="btn btn-primary btn-sm" onClick={crear} disabled={creando}>
            {creando ? <><span className="spinner" /> Creando…</> : 'Crear estancia'}
          </button>
        </div>
      </div>

      {/* Listado */}
      {error && <div className="alert alert-error"><span>⚠</span> {error}</div>}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}><span className="spinner spinner-lg" /></div>
      ) : estancias.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '2rem', color: 'var(--mid)' }}>Aún no hay estancias.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '.6rem' }}>
          {estancias.map(e => {
            const pagado = e.paymentStatus === 'completed';
            return (
              <div key={e.id} className="card" style={{ padding: '.8rem 1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ color: 'var(--white)', fontWeight: 600 }}>{e.familyName || e.contactEmail}</div>
                    <div style={{ color: 'var(--mid)', fontSize: '.82rem' }}>{e.contactEmail}</div>
                    <div style={{ color: 'var(--mid)', fontSize: '.8rem', marginTop: '.2rem' }}>
                      {e.checkInDate ? `🗓️ ${e.checkInDate}${e.checkOutDate ? ` → ${e.checkOutDate}` : ''}` : 'sin fechas'}
                      {e.concepto ? ` · ${e.concepto}` : ''}
                    </div>
                    <div style={{ marginTop: '.3rem', fontSize: '.75rem' }}>
                      <span style={{
                        padding: '.1rem .4rem', borderRadius: 6,
                        background: e.metodoPago === 'transferencia' ? 'rgba(232,164,48,.15)' : 'rgba(74,143,168,.15)',
                        color: e.metodoPago === 'transferencia' ? 'var(--warn)' : '#7fb3c8',
                      }}>
                        {e.metodoPago === 'transferencia' ? '🏦 Transferencia' : '💳 Stripe'}
                      </span>
                      {e.metodoPago === 'transferencia' && e.conceptoPago && (
                        <span style={{ color: 'var(--mid)', marginLeft: '.4rem' }}>Concepto: <strong style={{ color: 'var(--white)' }}>{e.conceptoPago}</strong></span>
                      )}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', fontSize: '.82rem', minWidth: 150 }}>
                    <div style={{ color: 'var(--white)', fontWeight: 600 }}>{money(e.paid)} / {money(e.total)}</div>
                    <div style={{ color: pagado ? 'var(--green-light)' : 'var(--warn)' }}>{pagado ? '✅ Pagado' : '🟡 Pendiente'}</div>
                    {(e.payments || []).map((p, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '.4rem', fontSize: '.75rem', marginTop: '.15rem' }}>
                        <span style={{ color: p.status === 'paid' ? 'var(--green-light)' : 'var(--mid)' }}>
                          {p.status === 'paid' ? '✓' : '·'} {p.label}: {money(p.amount)}
                        </span>
                        <button
                          onClick={() => marcarPlazo(e, p.planId, p.status !== 'paid')}
                          title={p.status === 'paid' ? 'Marcar como pendiente' : 'Marcar como cobrado'}
                          style={{ fontSize: '.66rem', padding: '.05rem .35rem', borderRadius: 5, cursor: 'pointer',
                            border: '1px solid rgba(255,255,255,.2)', background: 'transparent',
                            color: p.status === 'paid' ? 'var(--mid)' : 'var(--green-light)' }}>
                          {p.status === 'paid' ? '↺' : '✓ cobrado'}
                        </button>
                      </div>
                    ))}
                  </div>
                  <div style={{ minWidth: 120, textAlign: 'right' }}>
                    {e.asignada
                      ? <span className="badge" style={{ background: 'rgba(90,122,80,.18)', color: 'var(--green-light)' }}>Asignada</span>
                      : <button className="btn btn-ghost btn-sm" onClick={() => asignar(e)} title="La familia debe haber iniciado sesión">⚠️ Asignar familia</button>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
